import { Directive, ElementRef, input, Renderer2, OnInit, OnDestroy, PLATFORM_ID, inject, output } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Directive that adds a class when a sticky element becomes "stuck"
 * Uses scroll position with hysteresis for stable state changes
 * 
 * Usage: <div class="sticky top-0" stickyClass="is-stuck" [stickyHeightDiff]="140">
 */
@Directive({
  selector: '[stickyClass]',
  standalone: true
})
export class StickyClassDirective implements OnInit, OnDestroy {
  private readonly el = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);
  
  /** Class to add when element is stuck */
  readonly stickyClass = input<string>('is-stuck');
  
  /** Height difference to compensate scroll (expanded - collapsed) */
  readonly stickyHeightDiff = input<number>(0);
  
  /** Scroll threshold to become stuck (pixels from element's initial position) */
  readonly stickThreshold = input<number>(20);
  
  /** Scroll threshold to become unstuck (must scroll back this far above stick point) */
  readonly unstickThreshold = input<number>(5);
  
  /** Emit when stuck state changes */
  readonly stickyStateChange = output<boolean>();
  
  private isStuck = false;
  private scrollContainer: HTMLElement | Window | null = null;
  private useWindowScroll = false;
  private initialOffsetTop = 0;
  private boundScrollHandler: (() => void) | null = null;
  private rafId: number | null = null;

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    
    // Find scroll container (closest scrollable parent, or fall back to window)
    const container = this.findScrollContainer(this.el.nativeElement);
    if (container) {
      this.scrollContainer = container;
      this.useWindowScroll = false;
    } else {
      // Fall back to window scroll (common on mobile)
      this.scrollContainer = window;
      this.useWindowScroll = true;
    }
    
    // Calculate initial offset of element relative to scroll container
    this.initialOffsetTop = this.getElementOffsetTop();
    
    // Create scroll handler with RAF for performance
    this.boundScrollHandler = () => {
      if (this.rafId) return; // Skip if already scheduled
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.checkStickyState();
      });
    };
    
    this.scrollContainer.addEventListener('scroll', this.boundScrollHandler, { passive: true });
    
    // Initial check
    this.checkStickyState();
  }
  
  private getElementOffsetTop(): number {
    if (!this.scrollContainer) return 0;
    
    const elRect = this.el.nativeElement.getBoundingClientRect();
    
    if (this.useWindowScroll) {
      // For window scroll, offset is element's position relative to document top
      return elRect.top + window.scrollY;
    } else {
      const container = this.scrollContainer as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      // Element's position relative to scroll container's content
      return elRect.top - containerRect.top + scrollTop;
    }
  }
  
  private getScrollTop(): number {
    if (this.useWindowScroll) {
      return window.scrollY;
    }
    return (this.scrollContainer as HTMLElement).scrollTop;
  }
  
  private checkStickyState() {
    if (!this.scrollContainer) return;
    
    const scrollTop = this.getScrollTop();
    const stickPoint = this.initialOffsetTop;
    
    // Hysteresis: different thresholds for stick vs unstick
    const shouldStick = scrollTop > stickPoint + this.stickThreshold();
    const shouldUnstick = scrollTop < stickPoint + this.unstickThreshold();
    
    if (!this.isStuck && shouldStick) {
      // Become stuck
      this.applyStuckState(true);
    } else if (this.isStuck && shouldUnstick) {
      // Become unstuck
      this.applyStuckState(false);
    }
    // In the dead zone between thresholds: maintain current state
  }
  
  private findScrollContainer(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      const overflow = style.overflow + style.overflowY;
      if (/(auto|scroll)/.test(overflow)) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }
  
  private applyStuckState(stuck: boolean) {
    // Apply/remove class
    if (stuck) {
      this.renderer.addClass(this.el.nativeElement, this.stickyClass());
    } else {
      this.renderer.removeClass(this.el.nativeElement, this.stickyClass());
    }
    
    this.isStuck = stuck;
    this.stickyStateChange.emit(stuck);
    
    // Note: Scroll compensation disabled - it causes re-trigger loops
    // The visual jump on unstick is acceptable vs being stuck in a loop
  }

  ngOnDestroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.boundScrollHandler && this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.boundScrollHandler);
      this.boundScrollHandler = null;
    }
  }
}
